import { formatDateDisplay } from '@/utils/date-utils';
import Big from 'big.js';
import { LaunchStatus } from './table-types';

export const round = (n: number, decimals: number) => {
  return Number(new Big(n).round(decimals, Big.roundHalfUp).toFixed(decimals));
};

export const formatForDisplay = (
  n: number,
  decimals: number,
  withTrailingZeros?: boolean,
): string => {
  const rounded = Number(new Big(n).round(decimals, Big.roundHalfUp));

  return rounded.toLocaleString('da-DK', {
    minimumFractionDigits: withTrailingZeros ? decimals : Math.min(2, decimals),
    maximumFractionDigits: decimals,
  });
};

// use comma as the decimal separator instead of a dot
export const formatDecimal = (
  n: number,
  decimals: number,
  withTrailingZeros?: boolean,
): string => {
  const rounded = new Big(n).round(decimals, Big.roundHalfUp);
  return withTrailingZeros
    ? rounded.toFixed(decimals).replace('.', ',')
    : rounded.toString().replace('.', ',');
};

// shared with EditCellInput so the edit box and the no-op-edit guard use the same precision
export const priceDisplayDecimals = 2;
export const rmDisplayDecimals = 5;

export const formatYesNo = (value: boolean | null | undefined): string =>
  value ? 'Yes' : 'No';

export const formatBoolean = (value: boolean | null | undefined): '1' | '0' =>
  value ? '1' : '0';

export const formatDate = (date: Date | null | undefined): string =>
  date ? formatDateDisplay(date) : '';

export const formatPieceCount = (value: number | null | undefined): string =>
  value == null ? '' : value.toLocaleString('da-DK');

export const formatLaunchStatusDisplay = (
  status: keyof typeof LaunchStatus | null | undefined,
): string => (status ? LaunchStatus[status] : '');

export const formatPriceField = (
  value: number,
  withTrailingZeros = false,
  decimals = priceDisplayDecimals,
): string => formatForDisplay(value, decimals, withTrailingZeros);

const formatPercentField = (
  value: number,
  decimals: number,
  withTrailingZeros = true,
): string => `${formatDecimal(value * 100, decimals, withTrailingZeros)}%`;

export const formatFactorField = (value: number): string =>
  formatDecimal(value, 3, true);

export const formatRm = (value: number): string =>
  formatPercentField(value, 1, true);

export const formatForecastedDemand = (value: number): string =>
  round(value, 0).toLocaleString('da-DK');

export const formatVat = (value: number | null | undefined): string =>
  formatPercentField(value ?? 0, 2, false);

export const formatLpDelta = (value: number | null | undefined): string =>
  formatPercentField(value ?? 0, 1, false);

//indexes in simulation, above the table
export const formatSimulationCalculatedIndex = (value: number): string =>
  formatDecimal(value, 1, true);

//indexes in simulation and create price, in the table, like LL RRP Index
export const formatIndex = (value: number): number => round(value, 0);
